/**
 * Selects the appropriate organization from a list of discovered organizations.
 *
 * Used by both DiscoveryCallback (normal login) and ConnectedAppAuthorize
 * (Connected App OAuth consent) to auto-select an org after Stytch discovery.
 *
 * Selection rules:
 * 1. Single org → select it.
 * 2. Multiple orgs → match by email domain against org email_allowed_domains
 *    or claimed_email_domains. Exactly one match → select it.
 * 3. Multiple orgs, no domain match → match by org name/slug against email domain.
 *    Exactly one match → select it.
 * 4. Otherwise → return null (ambiguous, cannot auto-select).
 */
export function selectOrganization(
  discoveredOrganizations: any[],
  discoveredEmail: string
): any | null {
  if (!Array.isArray(discoveredOrganizations) || discoveredOrganizations.length === 0) {
    return null;
  }
  if (discoveredOrganizations.length === 1) {
    return discoveredOrganizations[0];
  }

  const emailDomain = discoveredEmail.includes('@') ? discoveredEmail.split('@')[1] : '';
  if (!emailDomain) {
    return null;
  }

  const domainMatches = discoveredOrganizations.filter((entry: any) =>
    organizationHasEmailDomain(entry?.organization, emailDomain)
  );
  if (domainMatches.length === 1) {
    return domainMatches[0];
  }
  if (domainMatches.length > 1) {
    return null;
  }

  const matches = discoveredOrganizations.filter((entry: any) => {
    const org = entry?.organization;
    if (!org) {
      return false;
    }
    const name =
      typeof org.organization_name === 'string' ? org.organization_name.trim().toLowerCase() : '';
    const slug =
      typeof org.organization_slug === 'string' ? org.organization_slug.trim().toLowerCase() : '';
    return name === emailDomain || slug === emailDomain;
  });

  if (matches.length === 1) {
    return matches[0];
  }

  return null;
}

export function organizationHasEmailDomain(org: any, emailDomain: string): boolean {
  if (!org || !emailDomain) {
    return false;
  }

  const normalizedDomain = emailDomain.trim().toLowerCase();
  const candidates: string[] = [];

  const collectDomains = (value: unknown) => {
    if (!Array.isArray(value)) {
      return;
    }
    for (const item of value) {
      if (typeof item === 'string') {
        candidates.push(item.trim().toLowerCase());
        continue;
      }
      if (
        item &&
        typeof item === 'object' &&
        typeof (item as { domain?: unknown }).domain === 'string'
      ) {
        candidates.push(((item as { domain: string }).domain || '').trim().toLowerCase());
      }
    }
  };

  collectDomains(org.email_allowed_domains);
  collectDomains(org.claimed_email_domains);

  return candidates.some((candidate) => candidate === normalizedDomain);
}
